import { switchMap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { JobResponse, JobType } from '@business/entities/job';
import { JobAPI } from '@business/api/job';
import { OrganizationAPI } from '@business/api/organization';
import { FlashMessage } from '@core/components/FlashMessage';
import { ClockinReportsAPI } from '@business/api/clockin_reports';
import { OvertimeAPI } from '@business/api/overtime';
import { TimeoffAPI } from '@business/api/timeoff';

export class JobBloc extends BaseBloc {
  private readonly jobsRepo = new Repository<JobResponse>({
    getItemId: job => job.jobId || job.id,
  });

  readonly jobs$ = this.jobsRepo.items$;
  readonly job$ = this.jobsRepo.selectItem;

  onReset() {
    this.jobsRepo.reset();
  }

  fetchJobs = () => {
    return JobAPI.fetchJobs().pipe(this.jobsRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_jobs_error') }));
  };

  dismissJob = (jobId: string) => {
    return JobAPI.dismissJob(jobId).pipe(
      this.jobsRepo.ops.removeOne(() => jobId),
      handleMessage({ error: requestMessage('dismiss_job_error') }),
    );
  };

  handleJobCompletion = (job: JobResponse) => {
    if (job.type === JobType.exportOrganizations) {
      OrganizationAPI.downloadExport(job.id, job.success!.filename).pipe(switchMap(this.fetchJobs)).subscribe();
    } else if ([JobType.importActivity, JobType.importContact, JobType.importContact].find(i => i === job.type)) {
      FlashMessage.success('Import Complete', 'Your import has completed successfully.');
      JobAPI.dismissJob(job.id).subscribe();
    } else if (job.type === JobType.exportClockin) {
      ClockinReportsAPI.downloadExport(job.id, job.success!.filename).pipe(switchMap(this.fetchJobs)).subscribe();
    } else if (job.type === JobType.exportTimeoff) {
      TimeoffAPI.downloadExport(job.id, job.success!.filename).pipe(switchMap(this.fetchJobs)).subscribe();
    } else if (job.type === JobType.exportOvertime) {
      OvertimeAPI.downloadExport(job.id, job.success!.filename).pipe(switchMap(this.fetchJobs)).subscribe();
    }
  };
}
